import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  type Router,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { getUser } from './api.js';
import { LoginPage } from './LoginPage.js';
import { RouteError } from '../components/RouteError.js';
import { Skeleton, SkeletonRegion } from '../design-system/index.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EVERY PAGE IS LAZY. THE LOGIN SCREEN USED TO DOWNLOAD THE FLOW BUILDER.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * These were static imports, so Rollup put all five pages — including `@xyflow/react`, which
 * is ~65 kB gzip on its own — into the one `shell` chunk that `index.html` loads. A signed
 * OUT visitor, who can reach exactly one page, was downloading the entire console in order to
 * look at a password field. This console is used from a phone on mobile data, which is where
 * that stops being merely untidy.
 *
 * `LoginPage` stays STATIC, deliberately: it is the one page a cold visitor is guaranteed to
 * need, and making it lazy would buy nothing while putting a second round trip in front of
 * the only thing they came for.
 *
 * ── THE EXPORT NAME IS PASSED EXPLICITLY, AND IT HAS TO BE ─────────────────
 * `lazyRouteComponent(importer)` with no second argument looks for a DEFAULT export. Every
 * page here is a named export, and the failure mode is not a compile error — it is a route
 * that resolves to `undefined` at run time, which reads as a blank shell rather than as an
 * obviously wrong import.
 */
const AuthenticatedShell = lazyRouteComponent(
  () => import('./AuthenticatedShell.js'),
  'AuthenticatedShell',
);
const DashboardPage = lazyRouteComponent(() => import('../admin/DashboardPage.js'), 'DashboardPage');
const ExtensionsPage = lazyRouteComponent(
  () => import('../admin/ExtensionsPage.js'),
  'ExtensionsPage',
);
const NumbersPage = lazyRouteComponent(() => import('../admin/NumbersPage.js'), 'NumbersPage');
const FlowsPage = lazyRouteComponent(() => import('../admin/flows/FlowsPage.js'), 'FlowsPage');
const QueuesPage = lazyRouteComponent(() => import('../admin/queues/QueuesPage.js'), 'QueuesPage');
const GreetingsPage = lazyRouteComponent(
  () => import('../admin/sounds/GreetingsPage.js'),
  'GreetingsPage',
);
const FlowBuilderPage = lazyRouteComponent(
  () => import('../admin/flows/FlowBuilderPage.js'),
  'FlowBuilderPage',
);
/*
 * Session 18's pages. Each was routed and in the nav from the start — as a placeholder stub
 * until it was built — so they could be built in parallel without every one of them editing
 * this file and `AppLayout`'s `NAV`. All are built now, and the placeholder is gone.
 */
const AlertsPage = lazyRouteComponent(() => import('../admin/alerts/AlertsPage.js'), 'AlertsPage');
const CallsPage = lazyRouteComponent(() => import('../admin/calls/CallsPage.js'), 'CallsPage');
const CallDetailPage = lazyRouteComponent(
  () => import('../admin/calls/CallDetailPage.js'),
  'CallDetailPage',
);
const WallboardPage = lazyRouteComponent(
  () => import('../admin/queues/WallboardPage.js'),
  'WallboardPage',
);
const CarrierPage = lazyRouteComponent(() => import('../admin/carrier/CarrierPage.js'), 'CarrierPage');
const HealthPage = lazyRouteComponent(() => import('../admin/health/HealthPage.js'), 'HealthPage');
const AuditPage = lazyRouteComponent(() => import('../admin/audit/AuditPage.js'), 'AuditPage');
const OutboundPage = lazyRouteComponent(
  () => import('../admin/outbound/OutboundPage.js'),
  'OutboundPage',
);
// Session 19 (design §9): what every phone may dial, for every role.
const DiallingRulesPage = lazyRouteComponent(
  () => import('../admin/dialling/DiallingRulesPage.js'),
  'DiallingRulesPage',
);
const AiAgentsPage = lazyRouteComponent(() => import('../admin/ai-agents/AiAgentsPage.js'), 'AiAgentsPage');
const SchedulesPage = lazyRouteComponent(
  () => import('../admin/schedules/SchedulesPage.js'),
  'SchedulesPage',
);
const AccessPage = lazyRouteComponent(() => import('../admin/access/AccessPage.js'), 'AccessPage');
const SystemPage = lazyRouteComponent(() => import('../admin/system/SystemPage.js'), 'SystemPage');

/**
 * The route tree, defined in CODE rather than generated from the filesystem.
 *
 * TanStack Router's file-based mode writes a `routeTree.gen.ts` that has to be regenerated
 * and committed. For a five-route console that is a build step and a merge-conflict surface
 * bought for nothing, and it hides the one thing worth reading — the auth guard below.
 *
 * ── THE GUARD IS ON A PATHLESS LAYOUT ROUTE, WHICH IS THE POINT ────────────
 * `authenticatedRoute` contributes no path segment; it exists solely so that `beforeLoad`
 * runs for every child. Putting the check on each page instead means the check is opt-in, and
 * the page somebody adds next year without it is a page that renders for a signed-out user
 * and then fails four times over on 401s. This is the same reasoning `api/src/routes/index.ts`
 * gives for registering `authenticate` per group rather than globally with a skip-list: make
 * the omission loud, not silent.
 *
 * ── AND IT READS THE MODULE, NOT A CONTEXT ─────────────────────────────────
 * `beforeLoad` runs outside React. `getUser()` is the same closure `api.ts`'s fetch wrapper
 * and `SessionProvider` both read, so there is one answer to "is there a session" rather than
 * two that can disagree during a redirect.
 */

const rootRoute = createRootRoute({
  component: (): ReactNode => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    // Already signed in: bounce to the dashboard rather than showing a form that will only
    // put them back where they started.
    if (getUser()) throw redirect({ to: '/' });
  },
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: ({ location }) => {
    if (!getUser()) {
      throw redirect({
        to: '/login',
        /**
         * Where they were going, so the login page can send them back. Carried in the search
         * params rather than in a module variable, because a module variable does not survive
         * the reload that a session expiry frequently comes with.
         */
        search: { redirect: location.href },
      });
    }
  },
  /**
   * The frame and the softphone, LAZILY — see `AuthenticatedShell.tsx` for why this one
   * import was worth more than splitting all five pages put together. The guard above runs
   * first and unchanged, so a signed-out visitor never fetches this chunk at all.
   */
  component: AuthenticatedShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  component: DashboardPage,
});

const extensionsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/extensions',
  component: ExtensionsPage,
});

const numbersRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/numbers',
  component: NumbersPage,
});

const flowsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/flows',
  component: FlowsPage,
});

const queuesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/queues',
  component: QueuesPage,
});

const greetingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/greetings',
  component: GreetingsPage,
});

/**
 * The builder reads its id from the path rather than from state, so a flow is linkable — the
 * operator can send "look at this one" to somebody, and a reload lands back on the same flow
 * instead of on the list.
 */
const flowBuilderRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/flows/$flowId',
  component: function FlowBuilderRoute(): ReactNode {
    const { flowId } = flowBuilderRoute.useParams();
    return <FlowBuilderPage flowId={flowId} />;
  },
});

const page = (path: string, component: typeof AlertsPage) =>
  createRoute({ getParentRoute: () => authenticatedRoute, path, component });

const alertsRoute = page('/alerts', AlertsPage);
const callsRoute = page('/calls', CallsPage);
const wallboardRoute = page('/wallboard', WallboardPage);
const carrierRoute = page('/carrier', CarrierPage);
const healthRoute = page('/health', HealthPage);
const auditRoute = page('/audit', AuditPage);
const outboundRoute = page('/outbound', OutboundPage);
const diallingRoute = page('/dialling', DiallingRulesPage);
const schedulesRoute = page('/schedules', SchedulesPage);
const aiAgentsRoute = page('/ai-agents', AiAgentsPage);
const accessRoute = page('/access', AccessPage);
const systemRoute = page('/system', SystemPage);

/** One call, by its `linkedid` — linkable for the same reason a flow is. */
const callDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/calls/$linkedid',
  component: function CallDetailRoute(): ReactNode {
    const { linkedid } = callDetailRoute.useParams();
    return <CallDetailPage linkedid={linkedid} />;
  },
});

/**
 * ── THE CATCH-ALL IS A CHILD OF THE GUARD, AND THAT IS THE WHOLE REASON IT EXISTS ──
 *
 * `defaultNotFoundComponent` alone renders at the ROOT, outside `authenticatedRoute` — which
 * `router.test.tsx` caught: a mistyped URL produced a bare "Page not found" with no
 * navigation, no header and no way back. Worse, it also escaped the auth guard, so a
 * signed-out visitor got a 404 rather than the login page and could tell a nonexistent path
 * from an existing one without a session.
 *
 * A splat route under the layout fixes both at once: an unknown path now matches the guarded
 * subtree, so `beforeLoad` runs and a signed-out user is redirected exactly as they would be
 * for a real page, while a signed-in one gets the 404 inside the shell with the navigation
 * still there.
 */
const notFoundRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '$',
  component: (): ReactNode => (
    <div className="space-y-2">
      <h1 className="text-xl font-semibold tracking-tight text-fg">Page not found</h1>
      <p className="text-sm text-fg-muted">That address does not match any page in the console.</p>
    </div>
  ),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authenticatedRoute.addChildren([
    dashboardRoute,
    alertsRoute,
    callsRoute,
    callDetailRoute,
    wallboardRoute,
    carrierRoute,
    healthRoute,
    auditRoute,
    extensionsRoute,
    numbersRoute,
    outboundRoute,
    diallingRoute,
    flowsRoute,
    flowBuilderRoute,
    greetingsRoute,
    queuesRoute,
    schedulesRoute,
    aiAgentsRoute,
    accessRoute,
    systemRoute,
    notFoundRoute,
  ]),
]);

export function buildRouter(): Router<typeof routeTree, 'never', true> {
  return createRouter({
    routeTree,
    // The console is served from `/` by nginx's SPA fallback, so there is no basepath.
    defaultPreload: 'intent',
    /**
     * ⚠️ WITHOUT THIS, A CRASHED PAGE SAYS "Something went wrong!" AND NOTHING ELSE.
     *
     * That is `CatchBoundary`'s fallback, and a real user hit it on a real phone with no way
     * to tell me what had happened and no way for me to find out — the failure did not
     * reproduce in jsdom. On a mobile browser the developer console is not realistically
     * reachable, so the error has to be ON THE PAGE or it may as well not exist.
     */
    defaultErrorComponent: ({ error, reset }) => <RouteError error={error} reset={reset} />,
    /**
     * Every page is a dynamic import now, so on a slow connection there is a real gap between
     * the navigation and the page. Without this the shell renders with an EMPTY content area,
     * which reads as "the page is broken" rather than "the page is loading" — and on mobile
     * data that gap is exactly where it is longest.
     *
     * `SkeletonRegion` carries the accessible announcement with it, so this is not a silent
     * blank for a screen reader either.
     */
    defaultPendingComponent: (): ReactNode => (
      <SkeletonRegion busy readyLabel="Page loaded" className="space-y-3">
        <Skeleton shape="block" className="h-7 w-48" />
        <Skeleton shape="block" className="h-4 w-full" />
        <Skeleton shape="block" className="h-4 w-2/3" />
      </SkeletonRegion>
    ),
    /**
     * The last-resort handler, for anything the splat route above cannot catch — a path that
     * fails to match even the authenticated subtree. `notFoundRoute` is what handles the
     * ordinary mistyped-URL case, inside the shell and behind the guard.
     */
    defaultNotFoundComponent: (): ReactNode => (
      <div className="p-6">
        <h1 className="text-xl font-semibold tracking-tight text-fg">Page not found</h1>
      </div>
    ),
  }) as Router<typeof routeTree, 'never', true>;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
